import React, { FC, useEffect, useState } from 'react';
import { Printer } from '~reducers';
import StepsComponent, { TroubleShootPageContent } from '../Components/StepsComponent';
import ReceiptsNotPrinting from '../Components/ReceiptsNotPrinting';
import BongsNotPrinting from '../Components/BongsNotPrinting';
import ContactSupport from '../Components/ContactSupport';

// Images
import phoneToPower from '../../Images/phone-to-power.png';
import lightPowerImage from '../../Images/star-power-light.png';
import starUsb from '../../Images/star-usb.png';
import mpopUsbIllusteration from '../../Images/mPop-usb.png';
import ipadCharging from '../../Images/charging.png';
import useI18n from '~Translations';
import { useLogger, usePrinterApi } from '~Hooks';

interface Props {
  printer: Printer;
  closeGuide: () => void;
  testPrint: () => void;
  assignStation: () => void;
  sessionId: string;
}

type STEPS =
  | 'CHARGING'
  | 'ISSUE_WITH_QUESTION'
  | 'RESTART'
  | 'LIGHTS_QUESTION'
  | 'CHECK_USB_OUTLET'
  | 'TEST_OTHER_CABLE'
  | 'CHECK_POWER_CABLE'
  | 'TEST_POWER'
  | 'NO_RECEIPT_PRINTING'
  | 'NO_BONGS_PRINTING'
  | 'CONTACT_SUPPORT';

const USBTroubleShooting: FC<Props> = ({
  printer,
  closeGuide,
  testPrint,
  assignStation,
  sessionId,
}) => {
  const logger = useLogger('printer-troubleshooting');
  const { i18n } = useI18n();
  const printerApi = usePrinterApi();
  const [currentStep, setCurrentStep] = useState<STEPS>('CHARGING');

  useEffect(() => {
    logger.info('Troubleshooting step: ' + currentStep, {
      troubleShooting: {
        printerId: printer.id,
        printerName: printerApi.getNameFromId(printer.id),
        model: printer.model,
        createdAt: printer.createdAt,
        lastPrinted: printer.lastPrinted,
        troubleShootingSessionId: sessionId,
      },
    });
  }, [currentStep]);

  const steps: {
    [key in STEPS]: TroubleShootPageContent<STEPS>;
  } = {
    CHARGING: {
      title: i18n('Printer.TS.USB.IpadCharging'),
      text: i18n('Printer.TS.USB.IpadCharging.Desc'),
      images: [
        {
          src: ipadCharging,
          height: 150,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.No'),
          next: 'LIGHTS_QUESTION',
        },
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'ISSUE_WITH_QUESTION',
        },
      ],
    },
    ISSUE_WITH_QUESTION: {
      title: i18n('Printer.TS.IssueWith.Question'),
      buttons: [
        {
          text: i18n('Printer.TS.IssueWith.Receipts'),
          next: 'NO_RECEIPT_PRINTING',
        },
        {
          text: i18n('Printer.TS.IssueWith.KitchenTickets'),
          next: 'NO_BONGS_PRINTING',
        },
        {
          text: i18n('Printer.TS.IssueWith.Everything'),
          next: 'RESTART',
        },
      ],
    },
    RESTART: {
      title: i18n('Printer.TS.USB.RestartPrinter'),
      text: i18n('Printer.TS.USB.RestartPrinter.Desc'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.TestPrint'),
          action: 'TEST_PRINT',
        },
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    LIGHTS_QUESTION: {
      title: i18n('Printer.TS.LightsQuestion'),
      images: [
        {
          src: lightPowerImage,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.No'),
          next: 'CHECK_POWER_CABLE',
        },
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'CHECK_USB_OUTLET',
        },
      ],
    },
    CHECK_USB_OUTLET: {
      title: i18n('Printer.TS.USB.CableOutlet.Question'),
      images: [
        {
          src: printer.model === 'mPOP' ? mpopUsbIllusteration : starUsb,
          height: 300,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.TestPrint'),
          action: 'TEST_PRINT',
        },
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'TEST_OTHER_CABLE',
        },
      ],
    },
    TEST_OTHER_CABLE: {
      title: i18n('Printer.TS.USB.TryOtherCable'),
      text: i18n('Printer.TS.USB.TryOtherCable.Desc'),
      images: [
        {
          src: printer.model === 'mPOP' ? mpopUsbIllusteration : starUsb,
          height: 300,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.TestPrint'),
          action: 'TEST_PRINT',
        },
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    CHECK_POWER_CABLE: {
      title: i18n('Printer.TS.CablePluggedIn'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.TestPrint'),
          action: 'TEST_PRINT',
        },
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'TEST_POWER',
        },
      ],
    },
    TEST_POWER: {
      title: i18n('Printer.TS.PowerWorking'),
      text: i18n('Printer.TS.PowerWorking.Desc'),
      images: [
        {
          src: phoneToPower,
          height: 250,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    NO_RECEIPT_PRINTING: {
      custom: (
        <ReceiptsNotPrinting
          printer={printer}
          closeGuide={closeGuide}
          testPrint={testPrint}
          printingWorks={(works) => setCurrentStep(works ? 'CONTACT_SUPPORT' : 'RESTART')}
          sessionId={sessionId}
        />
      ),
    },
    NO_BONGS_PRINTING: {
      custom: (
        <BongsNotPrinting
          printer={printer}
          closeGuide={closeGuide}
          goToContactSupport={() => setCurrentStep('CONTACT_SUPPORT')}
          assignStation={() => {
            closeGuide();
            assignStation();
          }}
          sessionId={sessionId}
        />
      ),
    },
    CONTACT_SUPPORT: {
      custom: <ContactSupport />,
    },
  };

  return (
    <StepsComponent<STEPS>
      steps={steps}
      testPrint={testPrint}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
    />
  );
};

export default USBTroubleShooting;
